/* eslint-disable no-await-in-loop */
import moment from "moment";
import axios from "axios";
import { toPairs } from "lodash";
import { version } from "../../package.json";
import {
  AWS_BASE_PATH, API_URL_ANGULAR, API_URL_ESV, ESV_PRIVATE_BUCKET, automatedServerRoute,
} from "./Constant";

export function pause(msec) {
  return new Promise(
    (resolve) => {
      setTimeout(resolve, msec || 1000);
    },
  );
}

export const getViewAsPerStatusCode = (error) => {
  if (error.request.status === 404 || (error.request.status >= 500 && error.request.status <= 599)) {
    window.location.pathname = "/maintenance";
  }
};

/* LOGIN */
export const isUser = () => {
  const user = (sessionStorage.getItem("user_details_automation")
  && JSON.parse(sessionStorage.getItem("user_details_automation")))
  || null;
  return !!(user && user.id);
};

export const getUser = () => {
  const user = (sessionStorage.getItem("user_details_automation")
    && JSON.parse(sessionStorage.getItem("user_details_automation")))
    || null;
  return {
    ...user,
  };
};

export const signOut = (param = "") => {
  sessionStorage.clear();
  window.location.assign(`/login${param}`);
};

export const hasPermission = (permissionCode) => {
  if (!permissionCode) {
    return false;
  }
  return Boolean(getUser()?.permissionMap?.[permissionCode]);
};

/* S3 */
const MAX_S3_RETRIES = 3;
const S3_RETRY_DELAY = 1000;

/** Fetch a presigned S3 URL for GET and POST */
export const getPresignedURL = async (payload, signal = null) => {
  try {
    const esvPresignedApiPath = `${API_URL_ESV}/s3_presigned_url/`;
    const smsPresignedApiPath = `${API_URL_ANGULAR}/get_presigned_url/`;
    let apiUrl = smsPresignedApiPath;
    if (payload?.aws_s3_urls?.[0]?.bucket === ESV_PRIVATE_BUCKET) {
      apiUrl = esvPresignedApiPath;
    }
    const currentUrl = window.location.href;
    if (currentUrl.includes(automatedServerRoute) && apiUrl === smsPresignedApiPath) { // Use the unauthenticated presigned url for the server send reports
      apiUrl = `${API_URL_ANGULAR}/get_presigned_url_server/`;
    }
    const response = await axios.post(apiUrl, payload, { signal });
    const data = await response.data;
    return { success: true, ...data };
  } catch (error) {
    return { success: false, error, aborted: error.code === "ERR_CANCELED" };
  }
};

/**
  * This is used when getting file from the S3 bucket we can convert JSON file from binary format to a JSON format
  * @param input a file in a binary format
  * @returns the file in a JSON format
  */
export const convertUnit8ArrayToJson = (input) => {
  // Step 1: Convert Uint8Array to string
  const text = new TextDecoder().decode(input);
  // Step 2: Parse the string as JSON
  const jsonObject = JSON.parse(text);
  return jsonObject;
};

/**
 * Fetch file from given s3 bucket
 * @param {string} path
 * @param {s3} s3 s3 bucket
 * @param {Boolean} appendBasePath whether to add AWS_BASE_PATH at the beginning of the filename (true for logs - Micro History, Chem History, Batch Report Log, Send Report Log. Since the GET-POST is handled by frontend, when we get the file from S3, we need to put the AWS_BASE_PATH for local testing.)
 * @param {Boolean} noCache whether cache control header should be "no-cache" or default
 * @param {string} fileType for content type "binary/octet-stream" it cannot be interpreted as PDF or JSON, in this case, we need to pass the file type, e.g Send Report Preview
 * @param {AbortSignal} signal AbortController signal
 * @returns {Object} { success: Boolean, blob: Blob, data: UInt8Array }
 */
export const getFileFromS3 = async (path, s3, appendBasePath = false, noCache = true, fileType = null, signal = null) => {
  const Name = `${appendBasePath ? AWS_BASE_PATH : ""}${path}`;
  const presignedData = await getPresignedURL({
    action: "get",
    urls: [{
      Bucket: s3.bucket,
      Name,
      Region: s3.region,
    }],
  }, signal);
  if (presignedData.success) {
    let attempts = 0;
    while (attempts < MAX_S3_RETRIES) {
      try {
        const response = await axios.get(presignedData.result[Name], {
          responseType: "blob",
          headers: {
            "Cache-Control": noCache ? "no-cache" : "",
          },
          signal,
        });
        const blob = await response.data;
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const fileBlob = new Blob([blob], { type: fileType ?? blob.type });
        return {
          success: true, blob: fileBlob, presignedFailed: false, data: uint8Array,
        };
      } catch (e) {
        attempts++;
        if (attempts >= S3_RETRY_DELAY || e?.response?.status !== 503 || signal?.aborted) { // if no more attempts or the current error is not 503 (SlowDown), then return the error
          const errorCode = e?.response?.status === 404 ? "NoSuchKey" : "";
          return {
            success: false, blob: "", data: "", presignedFailed: false, errorCode, aborted: signal?.aborted ?? false,
          };
        }
        await pause(S3_RETRY_DELAY);
      }
    }
  }
  return {
    success: false, presignedFailed: true, blob: "", data: "", aborted: signal?.aborted,
  };
};

/** Fetches a single S3 object from a presigned URL with retry handling. @param {string} presignedUrl - presigned GET url; @param {Boolean} noCache - cache control header; @param {string|null} fileType - blob content type override; @param {AbortSignal|null} signal - abort signal */
const fetchBlobFromPresignedUrl = async (presignedUrl, noCache = true, fileType = null, signal = null) => {
  let attempts = 0;
  while (attempts < MAX_S3_RETRIES) {
    try {
      const response = await axios.get(presignedUrl, {
        responseType: "blob",
        headers: {
          "Cache-Control": noCache ? "no-cache" : "",
        },
        signal,
      });
      const blob = await response.data;
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileBlob = new Blob([blob], { type: fileType ?? blob.type });
      return {
        success: true, blob: fileBlob, presignedFailed: false, data: uint8Array,
      };
    } catch (e) {
      attempts++;
      if (attempts >= MAX_S3_RETRIES || e?.response?.status !== 503 || signal?.aborted) {
        const errorCode = e?.response?.status === 404 ? "NoSuchKey" : "";
        return {
          success: false, blob: "", data: "", presignedFailed: false, errorCode, aborted: signal?.aborted ?? false,
        };
      }
      await pause(S3_RETRY_DELAY);
    }
  }
  return {
    success: false, presignedFailed: false, blob: "", data: "", aborted: signal?.aborted ?? false,
  };
};

/**
 * Fetch multiple files from S3 buckets
 * @param {Array<{bucket: string, name: string, region: string}>} urls S3 file descriptors
 * @param {Boolean} appendBasePath whether to add AWS_BASE_PATH at the beginning of the filename (true for logs - Micro History, Chem History, Batch Report Log, Send Report Log. Since the GET-POST is handled by frontend, when we get the file from S3, we need to put the AWS_BASE_PATH for local testing.)
 * @param {Boolean} noCache whether cache control header should be "no-cache" or default
 * @param {string|Array<string|null>|null} fileType content type override for all files or per-file
 * @param {AbortSignal} signal AbortController signal
 * @returns {Promise<Record<string, {success: Boolean, blob: Blob, data: UInt8Array}>>}
 */
export const getFilesFromS3 = async (urls = [], appendBasePath = false, noCache = true, fileType = null, signal = null) => {
  if (!urls.length) return {};

  const getFileKey = (path) => `${appendBasePath ? AWS_BASE_PATH : ""}${path}`;

  const presignedData = await getPresignedURL({
    action: "get",
    urls: urls.map((url) => ({
      Bucket: url.bucket,
      Name: getFileKey(url.path),
      Region: url.region,
    })),
  }, signal);

  if (!presignedData.success) {
    return urls.reduce((results, url) => ({
      ...results,
      [url.path]: {
        success: false, presignedFailed: true, blob: "", data: "", aborted: signal?.aborted,
      },
    }), {});
  }

  const fileResults = await Promise.all(urls.map(async (url, index) => {
    const fileKey = getFileKey(url.path);
    const presignedUrl = presignedData.result?.[fileKey];
    if (!presignedUrl) {
      return [url.path, {
        success: false, presignedFailed: true, blob: "", data: "", aborted: signal?.aborted,
      }];
    }

    const itemFileType = Array.isArray(fileType) ? fileType[index] : fileType;
    const fileResponse = await fetchBlobFromPresignedUrl(presignedUrl, noCache, itemFileType, signal);
    return [url.path, fileResponse];
  }));

  return Object.fromEntries(fileResults);
};

/**
 * Fetch the S3 file based on the presigned URL
 */
export const getFileFromS3WithUrl = async (path, presignedUrl, fileType = null, signal = null) => {
  let attempts = 0;
  while (attempts < MAX_S3_RETRIES) {
    try {
      const response = await axios.get(presignedUrl, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
        },
        signal,
      });
      const blob = await response.data;
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const fileBlob = new Blob([blob], { type: fileType ?? blob.type });
      return {
        success: true, blob: fileBlob, presignedFailed: false, data: uint8Array,
      };
    } catch (e) {
      attempts++;
      if (attempts >= S3_RETRY_DELAY || e?.response?.status !== 503) { // if no more attempts or the current error is not 503 (SlowDown), then return the error
        const errorCode = e?.response?.status === 404 ? "NoSuchKey" : "";
        return {
          success: false, blob: "", data: "", presignedFailed: false, errorCode, aborted: signal?.aborted ?? false,
        };
      }
      await pause(S3_RETRY_DELAY);
    }
  }
  return {
    success: false, presignedFailed: false, blob: "", data: "", aborted: signal?.aborted ?? false,
  };
};

/** Fetch a list of items from a S3 folder */
export const getListOfFilesFromS3 = async (path, s3) => {
  const presignedData = await getPresignedURL({
    action: "getall",
    urls: [{
      Bucket: s3.bucket,
      Name: path,
      Region: s3.region,
    }],
  });

  return presignedData;
};

/**
 * This function can be used to download file from S3 with or without the presigned URL
 * @param {*} path s3 path
 * @param {*} s3 s3 bucket object
 * @param {*} fileName file name
 * @param {*} presignedUrl
 */
export const downloadFileFromS3 = async (path, s3, fileName = "", presignedUrl = null, signal = null) => {
  const { blob: fileBlob, presignedFailed, aborted } = presignedUrl ? await getFileFromS3WithUrl(path, presignedUrl, null, signal) : await getFileFromS3(path, s3, false, true, null, signal);
  if (fileBlob) {
    const url = window.URL.createObjectURL(fileBlob);
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || path;
      a.click();
    }
    return { success: true, presignedFailed };
  }
  return { success: false, presignedFailed, aborted };
};

/** Upload file to a S3 bucket */
export const uploadFileToS3 = async (fileInfo, s3, appendBasePath = false) => {
  // key is path + name of the file
  const { file, key, type } = fileInfo;
  if (!file) {
    return { path: "", type };
  }
  const Name = `${appendBasePath ? AWS_BASE_PATH : ""}${key}`;
  const isEsvBucket = s3.bucket === ESV_PRIVATE_BUCKET;
  const presignedPayload = isEsvBucket ? {
    action: "post",
    aws_s3_urls: [
      {
        bucket: s3.bucket,
        path: Name,
        region: s3.region,
        content_type: file.type,
      },
    ],
  } : {
    action: "post",
    urls: [{
      Bucket: s3.bucket,
      Name,
      Region: s3.region,
      ContentType: file.type,
    }],
  };
  const presignedData = await getPresignedURL(presignedPayload);
  if (presignedData.success) {
    let attempts = 0;
    while (attempts < MAX_S3_RETRIES) {
      try {
        const { url, fields } = presignedData.result[Name];
        // Create FormData
        const formData = new FormData();
        toPairs(fields).forEach(([fieldKey, fieldValue]) => {
          if (fieldKey !== "Content-Type") {
            formData.append(fieldKey, fieldValue);
          }
        });
        // Add the file
        formData.append("file", file);
        // POST to S3
        await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        return { success: true, path: Name, type: file.type ? file.type : "" };
      } catch (e) {
        attempts++;
        if (attempts >= S3_RETRY_DELAY || e?.response?.status !== 503) { // if no more attempts or the current error is not 503 (SlowDown), then return the error
          return { success: false };
        }
        await pause(S3_RETRY_DELAY);
      }
    }
  }
  return { success: false, presignedFailed: true };
};

/** Delete file from a S3 folder */
export const deleteFileFromS3 = async (path, s3) => getPresignedURL({
  action: "delete",
  urls: [{
    Bucket: s3.bucket,
    Name: path,
    Region: s3.region,
  }],
});

export const autoRefresh = () => {
  setInterval(() => {
    // Get the current date and time
    const currentTime = new Date();

    // Check if the current time is 7:01 AM
    if (currentTime.getHours() === 7 && currentTime.getMinutes() === 1) {
      window.location.reload();
    }
  }, 25000);
};

export const getTestStatusClassName = (status) => {
  const statusMap = {
    Pending: "pending-test",
    Ready: "ready-test",
    "Ready-P": "readyP-test",
    "Hold-Micro": "microHold-test",
    Retest: "retest-test",
    Complete: "complete-test",
  };
  return statusMap[status];
};

/**
   * Convert the user permission list to an object
   * @return { string, boolean } => { code: true }
   */
export const createUserPermissionMap = (user) => {
  const { results } = user;
  const permissionMap = { "no-permission": true };
  const permissionGroups = ["automation", "global"];
  permissionGroups.forEach((group) => {
    // Store the existed permissions
    if (results[group]) {
      results[group].forEach((tab) => {
        tab.permissions.forEach((permission) => {
          const { code } = permission;
          permissionMap[code] = true;
        });
      });
    }
  });
  return permissionMap;
};

export const convertUtcToLocal = (utcTime) => moment.utc(utcTime).local().format();

/**
 * Get app version
 * @returns {string} app version
 */
export const getAppVersion = () => version;

/**
   * Format an date array from the RangePicker to MM/DD/YYYY-MM/DD/YYYY
   * @param {*} input either null or an array or date with length 2
   * @returns MM/DD/YYYY-MM/DD/YYYY
   */
export const formatAntdDateRange = (input, format = "MM/DD/YYYY", separator = "-") => {
  if (!input || input.length < 2) return "";

  const startDate = moment(input[0].$d).format(format);
  const endDate = moment(input[1].$d).format(format);

  return `${startDate}${separator}${endDate}`;
};

/**
 * Fetch the ESV auth token
 * @returns { success: boolean, message: string, id_token: string, refresh_token: string, company_domain: string, company_name: string, admin_user_payload: string, expires_in: number }
 */
export const fetchEsvAuthToken = async () => {
  try {
    const response = await axios.get(`${API_URL_ANGULAR}/get_esv_auth_token/`);
    const data = await response.data;
    return { success: true, ...data };
  } catch (error) {
    return { success: false, message: error.response?.data?.detail || "Something went wrong." };
  }
};

/**
 * Serializes params for GET query string.
 * Uses URLSearchParams so special chars (+, &, =, etc.) are encoded correctly
 * and not misinterpreted by the server.
 * @param {Record<string, unknown>} params
 * @returns {string}
 */
export const serializeQueryParams = (params) => {
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== ""),
  );
  return new URLSearchParams(filtered).toString();
};

/**
 * Convert a string to title case, for example: "hello world" -> "Hello World"
 * @param {*} str
 * @returns {string} title case string
 */
export const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
